import type {
  AuthUser,
  Checkpoint,
  CheckpointType,
  NotificationChannel,
  NotificationStatus,
  Project,
  ProjectStatus,
  Runbook,
} from '../types';

const API_BASE_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

const apiFetch = (url: string, options: RequestInit = {}) =>
  fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers ?? {}) },
    ...options,
  });

async function handleResponse<T>(response: Response): Promise<T> {
  const isJson = response.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const message = body?.message || body?.error || `Forespørsel feilet (${response.status})`;
    throw new ApiError(message, response.status, body?.code || body?.error);
  }

  return (body?.data ?? body) as T;
}

// --- Auth --------------------------------------------------------------

export const login = (email: string, password: string): Promise<{ user: AuthUser }> =>
  apiFetch(`${API_BASE_URL}/auth/login`, { method: 'POST', body: JSON.stringify({ email, password }) }).then((r) =>
    handleResponse(r),
  );

export const logout = (): Promise<void> =>
  apiFetch(`${API_BASE_URL}/auth/logout`, { method: 'POST' }).then(() => undefined);

export const getSession = (): Promise<{ authenticated: boolean; user?: AuthUser }> =>
  apiFetch(`${API_BASE_URL}/auth/session`).then((r) => handleResponse(r));

export const validateInvitation = (
  token: string,
): Promise<{ email: string; name: string; role: string; expiresAt: string }> =>
  apiFetch(`${API_BASE_URL}/auth/invitations/${encodeURIComponent(token)}`).then((r) => handleResponse(r));

export const acceptInvitation = (token: string, name: string, password: string): Promise<{ user: AuthUser }> =>
  apiFetch(`${API_BASE_URL}/auth/invitations/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
    body: JSON.stringify({ name, password }),
  }).then((r) => handleResponse(r));

export const requestPasswordReset = (email: string): Promise<{ message: string }> =>
  apiFetch(`${API_BASE_URL}/auth/password-reset/request`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  }).then((r) => handleResponse(r));

export const confirmPasswordReset = (token: string, password: string): Promise<{ message: string }> =>
  apiFetch(`${API_BASE_URL}/auth/password-reset/confirm`, {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  }).then((r) => handleResponse(r));

// --- Projects ----------------------------------------------------------

export const getProjects = (): Promise<Project[]> =>
  apiFetch(`${API_BASE_URL}/projects`).then((r) => handleResponse(r));

export const getProject = (id: string): Promise<Project> =>
  apiFetch(`${API_BASE_URL}/projects/${id}`).then((r) => handleResponse(r));

export interface CreateProjectInput {
  name: string;
  slug: string;
  category: string;
  status?: ProjectStatus;
  currentStage?: string | null;
  description?: string | null;
  copyablePrompt?: string | null;
}

export const createProject = (data: CreateProjectInput): Promise<Project> =>
  apiFetch(`${API_BASE_URL}/projects`, { method: 'POST', body: JSON.stringify(data) }).then((r) => handleResponse(r));

export const updateProject = (id: string, data: Partial<CreateProjectInput>): Promise<Project> =>
  apiFetch(`${API_BASE_URL}/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }).then((r) =>
    handleResponse(r),
  );

export const deleteProject = (id: string): Promise<void> =>
  apiFetch(`${API_BASE_URL}/projects/${id}`, { method: 'DELETE' }).then(() => undefined);

export const reorderProjects = (orderedIds: string[]): Promise<void> =>
  apiFetch(`${API_BASE_URL}/projects/reorder/priority`, {
    method: 'PATCH',
    body: JSON.stringify({ orderedIds }),
  }).then(() => undefined);

// --- Checkpoints -------------------------------------------------------

export interface CreateCheckpointInput {
  projectId: string;
  title: string;
  type?: CheckpointType;
  notes?: string | null;
}

export const createCheckpoint = (data: CreateCheckpointInput): Promise<Checkpoint> =>
  apiFetch(`${API_BASE_URL}/checkpoints`, { method: 'POST', body: JSON.stringify(data) }).then((r) =>
    handleResponse(r),
  );

export const updateCheckpoint = (id: string, data: Partial<Checkpoint>): Promise<Checkpoint> =>
  apiFetch(`${API_BASE_URL}/checkpoints/${id}`, { method: 'PATCH', body: JSON.stringify(data) }).then((r) =>
    handleResponse(r),
  );

export const resolveCheckpoint = (id: string, notes?: string): Promise<Checkpoint> =>
  apiFetch(`${API_BASE_URL}/checkpoints/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify(notes !== undefined ? { notes } : {}),
  }).then((r) => handleResponse(r));

export const deleteCheckpoint = (id: string): Promise<void> =>
  apiFetch(`${API_BASE_URL}/checkpoints/${id}`, { method: 'DELETE' }).then(() => undefined);

// --- Runbook -----------------------------------------------------------

export const getRunbook = (projectId: string): Promise<Runbook> =>
  apiFetch(`${API_BASE_URL}/projects/${projectId}/runbook`).then((r) => handleResponse(r));

export const saveRunbook = (
  projectId: string,
  content: string,
  opts: { expectedModifiedAt?: string | null; force?: boolean } = {},
): Promise<{ path: string; modifiedAt: string }> =>
  apiFetch(`${API_BASE_URL}/projects/${projectId}/runbook`, {
    method: 'PUT',
    body: JSON.stringify({ content, expectedModifiedAt: opts.expectedModifiedAt ?? null, force: opts.force ?? false }),
  }).then((r) => handleResponse(r));

// --- Notifications -----------------------------------------------------

export const getNotificationStatus = (): Promise<NotificationStatus> =>
  apiFetch(`${API_BASE_URL}/notifications/status`).then((r) => handleResponse(r));

export const getChannels = (projectId?: string): Promise<NotificationChannel[]> =>
  apiFetch(`${API_BASE_URL}/notifications/channels${projectId ? `?projectId=${projectId}` : ''}`).then((r) =>
    handleResponse(r),
  );

export interface CreateChannelInput {
  projectId?: string | null;
  channel: 'email' | 'sms' | 'telegram';
  target: string;
  triggerOn: string;
  enabled?: boolean;
}

export const createChannel = (data: CreateChannelInput): Promise<NotificationChannel> =>
  apiFetch(`${API_BASE_URL}/notifications/channels`, { method: 'POST', body: JSON.stringify(data) }).then((r) =>
    handleResponse(r),
  );

export const updateChannel = (id: string, data: Partial<CreateChannelInput>): Promise<NotificationChannel> =>
  apiFetch(`${API_BASE_URL}/notifications/channels/${id}`, { method: 'PATCH', body: JSON.stringify(data) }).then((r) =>
    handleResponse(r),
  );

export const deleteChannel = (id: string): Promise<void> =>
  apiFetch(`${API_BASE_URL}/notifications/channels/${id}`, { method: 'DELETE' }).then(() => undefined);

export const testChannel = (id: string): Promise<{ delivered: boolean; skipped: boolean; reason?: string }> =>
  apiFetch(`${API_BASE_URL}/notifications/channels/${id}/test`, { method: 'POST' }).then((r) => handleResponse(r));
